/**
 * The body the Fix Location screen sends when the person presses Save.
 *
 * `pinPlacedByHand` tells the server that a person put this pin where it is (dragged the marker, or typed the
 * coordinates). The server cannot work that out from the numbers alone when they equal the pin already stored, and that
 * is exactly the case on a retry after PIN_NOT_LOCKED: the first save stored the pin but could not mark it as the
 * person's, so the second save sends the same coordinates. Without the flag the retry would look like an echo and the
 * pin would stay unmarked, open to being replaced by an automatic address lookup.
 *
 * The flag is only sent with a complete pin, and never for a pin that is merely the stored one echoed back or one Google
 * found (a lookup's pin is not the person's).
 */

export interface FixLocationSaveInput {
  readonly address: { readonly address1: string; readonly address2: string; readonly city: string; readonly state: string; readonly zip: string };
  readonly latitude: number | null;
  readonly longitude: number | null;
  /** The person dragged the marker or typed a coordinate, and no lookup has replaced that pin since. */
  readonly pinPlacedByHand: boolean;
}

export interface FixLocationSaveBody {
  readonly address: FixLocationSaveInput['address'];
  readonly latitude: string | null;
  readonly longitude: string | null;
  readonly pinPlacedByHand?: true;
}

export function fixLocationSaveBody(input: FixLocationSaveInput): FixLocationSaveBody {
  const complete = input.latitude !== null && input.longitude !== null;
  return {
    address: input.address,
    latitude: input.latitude !== null ? String(input.latitude) : null,
    longitude: input.longitude !== null ? String(input.longitude) : null,
    ...(input.pinPlacedByHand && complete ? { pinPlacedByHand: true as const } : {}),
  };
}
